import { Auth } from "aws-amplify";
import { setRecoil } from "recoil-nexus";
import { accessTokenState } from "../../state/atoms/accessToken";
import { handleAuthError } from "./authErrorHandler";

export class TokenManager {
    private static refreshInterval: NodeJS.Timeout | null = null;
    private static readonly REFRESH_THRESHOLD = 10 * 60 * 1000; // 10 minutes before expiry

    /**
     * Initialize token management with automatic refresh
     */
    static async initialize(): Promise<void> {
        try {
            // Check current session
            const session = await Auth.currentSession();
            const accessToken = session.getAccessToken();
            const expirationTime = accessToken.getExpiration() * 1000; // Convert to milliseconds
            const currentTime = Date.now();
            
            // Set the current token
            setRecoil(accessTokenState, accessToken.getJwtToken());
            
            // Calculate time until refresh is needed
            const timeUntilRefresh = expirationTime - currentTime - this.REFRESH_THRESHOLD;
            
            if (timeUntilRefresh > 0) {
                // Schedule refresh
                this.scheduleRefresh(timeUntilRefresh);
            } else {
                // Token is already close to expiry, refresh immediately
                await this.refreshToken();
            }
        } catch (error) {
            console.log("Failed to initialize token manager:", error);
            // Don't throw here, as the user might not be logged in
        }
    }

    /**
     * Schedule a token refresh
     */
    private static scheduleRefresh(delay: number): void {
        // Clear any existing interval
        if (this.refreshInterval) {
            clearTimeout(this.refreshInterval);
        }
        
        this.refreshInterval = setTimeout(async () => {
            try {
                await this.refreshToken();
            } catch (error) {
                console.error("Scheduled token refresh failed:", error);
            }
        }, delay);
    }

    /**
     * Refresh the current token
     */
    static async refreshToken(): Promise<string | null> {
        try {
            // Force a session refresh
            await Auth.currentAuthenticatedUser();
            const session = await Auth.currentSession();
            const accessToken = session.getAccessToken();
            
            // Update the token in state
            const token = accessToken.getJwtToken();
            setRecoil(accessTokenState, token);
            
            // Schedule next refresh
            const expirationTime = accessToken.getExpiration() * 1000;
            const currentTime = Date.now();
            const timeUntilRefresh = expirationTime - currentTime - this.REFRESH_THRESHOLD;
            
            if (timeUntilRefresh > 0) {
                this.scheduleRefresh(timeUntilRefresh);
            }
            
            return token;
        } catch (error: any) {
            console.error("Token refresh failed:", error);
            
            // Handle auth errors
            await handleAuthError(error);
            return null;
        }
    }

    /**
     * Check if the current token is valid
     */
    static async isTokenValid(): Promise<boolean> {
        try {
            const session = await Auth.currentSession();
            const accessToken = session.getAccessToken();
            const expirationTime = accessToken.getExpiration() * 1000;
            const currentTime = Date.now();
            
            // Token is valid if it expires more than 5 minutes from now
            return (expirationTime - currentTime) > (5 * 60 * 1000);
        } catch (error) {
            return false;
        }
    }

    /**
     * Get the current access token
     */
    static async getCurrentToken(): Promise<string | null> {
        try {
            const session = await Auth.currentSession();
            return session.getAccessToken().getJwtToken();
        } catch (error) {
            return null;
        }
    }

    /**
     * Clean up the token manager
     */
    static cleanup(): void {
        if (this.refreshInterval) {
            clearTimeout(this.refreshInterval);
            this.refreshInterval = null;
        }
    }
} 